import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Avatar,
  TextField,
  InputAdornment,
  TablePagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add,
  Today,
  NavigateBefore,
  NavigateNext,
  Block,
  PictureAsPdf,
  Search,
  EditCalendar
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { format, startOfMonth, addMonths, subMonths, parseISO } from 'date-fns';

import { dateTimeUtils } from '../../utils/dateTimeUtils';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { useTimezone } from '../../hooks/useTimezone';
import { useSessionContext } from '../../context/SessionContext';
import { useAuth } from '../../context/AuthContext';

import attendanceService, {
  AttendanceService,
  MonthlyAttendanceData,
  StudentAttendanceRecord
} from '../../services/attendanceService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';

import {
  Session,
  SessionClass,
  SectionInstance
} from '../../types/sessions';
import {
  AttendanceStatus,
  InformationType
} from '../../types';

import StudentMarkAttendanceModal, { StudentSavePayload, StudentData } from '../../components/attendance/StudentMarkAttendanceModal';
import StudentOverrideAttendanceModal from '../../components/attendance/StudentOverrideAttendanceModal';
import AttendanceMarkBar from '../../components/attendance/AttendanceMarkBar';
import AttendanceCellPalette, { CellPaletteValue } from '../../components/attendance/AttendanceCellPalette';
import { STATUS_VISUAL, INFO_INDICATOR_STATUSES, INFO_DOT } from '../../utils/attendanceStatus';
import { usePdfExport, useSchoolBranding, fmtClassName } from '../../utils/pdf';
import AttendanceRegisterDocument from '../../components/attendance/pdf/AttendanceRegisterDocument';
import BusyIcon from '../../components/common/BusyIcon';

const EMPTY_VISUAL = { text: '·', bg: 'transparent', border: 'transparent' };

interface OverrideTarget {
  attendanceId: string;
  studentName: string;
  rollNumber: string;
  date: string;
  status?: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

// ---------------------------------------------------------------------------
// Mark Mode (inline column editing)
// ---------------------------------------------------------------------------
// Each draft mirrors what the user has staged for one student on the
// active day. Drafts are committed in a single `markBulkAttendance` call
// when the user hits Save in the floating MarkBar. `saved*` fields are a
// snapshot taken on edit-mode entry so we can compute and warn before
// overwriting previously-saved values.
interface DraftEntry {
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
  savedStatus?: AttendanceStatus;
  savedInfoType?: InformationType;
  savedRemarks?: string;
  hadSavedRecord: boolean;
  // User explicitly chose "Not marked" for this student on the active day —
  // the entry stays in draftMap so the sync effect doesn't re-add it, but
  // Save filters it out. Any previously-saved record is left untouched.
  cleared?: boolean;
}

interface PaletteAnchor {
  el: HTMLElement;
  studentId: string;
  studentName: string;
  rollNumber: string;
}

// Order of statuses when a cell is single-clicked in Mark Mode. Kept short
// (4 items) so the cycle stays fast for the common case. Leave / Holiday /
// Weekly-Off live in the popover palette, not in the cycle.
const CYCLE_ORDER: AttendanceStatus[] = [
  AttendanceStatus.PRESENT,
  AttendanceStatus.ABSENT,
  AttendanceStatus.LATE,
  AttendanceStatus.HALF_DAY
];

const NEEDS_INFO_TYPE = new Set<AttendanceStatus>([
  AttendanceStatus.LATE,
  AttendanceStatus.HALF_DAY,
  AttendanceStatus.ABSENT
]);

function nextStatusInCycle(current: AttendanceStatus): AttendanceStatus {
  const idx = CYCLE_ORDER.indexOf(current);
  return CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
}

const AttendancePage: React.FC = () => {
  const theme = useTheme();
  const { canRead, canWrite, isTeacher } = useModulePermissions();
  const { decodedToken } = useAuth();
  const { getToday } = useTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession } = useSessionContext();

  // ---- Filter state (Session → Class → Section, matching EnrollmentsPage) ----
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));

  // ---- Paging / search ----
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // ---- Data ----
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState<MonthlyAttendanceData | null>(null);

  // ---- Modals ----
  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [selectedModalDate, setSelectedModalDate] = useState<Date>(new Date());
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // ---- Mark Mode (inline column editing) ----
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [draftMap, setDraftMap] = useState<Record<string, DraftEntry>>({});
  const [paletteAnchor, setPaletteAnchor] = useState<PaletteAnchor | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  const canViewAttendance = canRead('student_attendance');
  const canMarkAttendance = canWrite('student_attendance');

  // Past-session reads are allowed; only the active session is writable.
  const isViewingActive = !!activeSession && selectedSession === activeSession._id;

  // Class-teacher UI gate — mirrors the backend's `canWriteAttendanceForSection`.
  // Only Teacher-role holders are restricted to sections they own. Admins
  // and any custom role with the .write scope pass unconditionally.
  const selectedSectionDoc = useMemo(
    () => sections.find(s => s._id === selectedSection) ?? null,
    [sections, selectedSection]
  );
  const isClassTeacherForSelected = useMemo(() => {
    if (!selectedSectionDoc || !decodedToken?.userId) return false;
    const ctId = selectedSectionDoc.classTeacherId;
    const owner = typeof ctId === 'object' && ctId !== null ? (ctId as any)._id : ctId;
    return !!owner && String(owner) === String(decodedToken.userId);
  }, [selectedSectionDoc, decodedToken?.userId]);
  const sectionWritable = !isTeacher
    ? !!selectedClass
    : !!selectedSection && isClassTeacherForSelected;
  const writable = canMarkAttendance && isViewingActive && sectionWritable;

  // The banner only surfaces a *meaningful* blocker — once a section is
  // chosen and the Teacher isn't its homeroom. Default "pick a section"
  // state is silent (the dropdown above is the obvious next step).
  const unauthorizedReason: string | null = useMemo(() => {
    if (!canMarkAttendance || !isViewingActive) return null;
    if (!isTeacher) return null;
    if (selectedSection && !isClassTeacherForSelected) {
      return 'You are not the class teacher for this section.';
    }
    return null;
  }, [canMarkAttendance, isViewingActive, isTeacher, selectedSection, isClassTeacherForSelected]);

  // ---- Load sessions + classes on mount ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allSessions, allClasses] = await Promise.all([
          sessionsApi.list(),
          // Same scoping the section dropdown uses below — Teachers only see
          // classes where they're the homeroom of at least one section (or
          // homeroom of a sectionless class).
          classesApi.list({ relationship: 'class-teacher' })
        ]);
        if (cancelled) return;
        setSessions(allSessions);
        setClasses(allClasses);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Could not load filters', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  // ---- Auto-default to active session once it's known ----
  useEffect(() => {
    if (!selectedSession && activeSession) {
      setSelectedSession(activeSession._id);
    }
  }, [activeSession, selectedSession]);

  // ---- Load sections when session + class changes ----
  useEffect(() => {
    if (!selectedSession || !selectedClass) {
      setSections([]);
      setSelectedSection('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await sectionInstancesApi.list({
          sessionId: selectedSession,
          classId: selectedClass,
          // Attendance is owned by the homeroom — Teachers should only see
          // sections they're class-teacher of. Server ignores this for
          // admins and other roles.
          relationship: 'class-teacher'
        });
        if (cancelled) return;
        const list = data || [];
        setSections(list);
        // Teachers must not see "All sections" — picking it would return
        // students from sections they don't own. Auto-select the first
        // owned section so the table immediately shows only relevant rows.
        // Admins (etc.) keep the "All sections" default.
        if (isTeacher && list.length > 0) {
          setSelectedSection(list[0]._id);
        } else {
          setSelectedSection('');
        }
      } catch (err) {
        if (!cancelled) console.error('Error loading sections:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSession, selectedClass, isTeacher]);

  // ---- Load monthly attendance ----
  const loadMonthlyAttendance = useCallback(async () => {
    if (!selectedSession || !selectedClass) {
      setMonthlyData(null);
      setTotalCount(0);
      return;
    }
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const data = await attendanceService.getMonthlyAttendance(
        selectedSession,
        selectedClass,
        year,
        month,
        {
          sectionInstanceId: selectedSection || undefined,
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearchQuery || undefined
        }
      );
      setMonthlyData(data);
      setTotalCount(data.pagination?.totalItems || 0);
    } catch (err: any) {
      console.error('Error loading monthly attendance:', err);
      enqueueSnackbar(err.message || 'Error loading attendance', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedSession, selectedClass, selectedSection, currentMonth, page, rowsPerPage, debouncedSearchQuery, enqueueSnackbar]);

  useEffect(() => { loadMonthlyAttendance(); }, [loadMonthlyAttendance]);

  // Reset to page 1 when search query or scope changes — otherwise the user
  // gets stuck on an empty page 4 after filtering.
  useEffect(() => { setPage(0); }, [debouncedSearchQuery, selectedSession, selectedClass, selectedSection]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
  };

  // ---- Student search for the mark modal ----
  // Uses enrollmentsApi which returns rows with populated `student` sub-docs.
  // No N+1 calls; backend handles the name/roll regex.
  const handleStudentSearch = useCallback(async (query: string): Promise<StudentData[]> => {
    if (!selectedSession || !selectedClass) return [];
    try {
      const { data } = await enrollmentsApi.list({
        sessionId: selectedSession,
        classId: selectedClass,
        sectionInstanceId: selectedSection || undefined,
        status: 'enrolled',
        search: query,
        page: 1,
        limit: 20
      });
      return (data as any[]).map(row => ({
        _id: String(row.studentId),
        name: row.student?.name ?? '',
        rollNumber: String(row.rollNumber ?? row.student?.rollNumber ?? ''),
        fatherName: row.student?.fatherName,
        phone: row.student?.phone
      }));
    } catch (err) {
      console.error('Error searching students:', err);
      return [];
    }
  }, [selectedSession, selectedClass, selectedSection]);

  const handleSaveAttendance = async (payload: StudentSavePayload) => {
    if (!selectedSession || !selectedClass) {
      enqueueSnackbar('Pick a session and class first', { variant: 'warning' });
      return;
    }
    if (!isViewingActive) {
      enqueueSnackbar('Attendance can only be marked for the active session', { variant: 'warning' });
      return;
    }
    try {
      const dateStr = AttendanceService.formatDateForAPI(selectedModalDate);
      const result = await attendanceService.markBulkAttendance({
        sessionId: selectedSession,
        classId: selectedClass,
        sectionInstanceId: selectedSection || undefined,
        date: dateStr,
        attendanceRecords: payload.applyToAll ? undefined : payload.attendanceRecords,
        applyToAll: payload.applyToAll,
        status: payload.applyToAll ? (payload.bulkStatus as AttendanceStatus) : undefined,
        informationType: payload.applyToAll ? (payload.bulkInfoType as InformationType) : undefined,
        remarks: payload.applyToAll ? payload.bulkRemarks : undefined
      });

      const { summary } = result;
      if (summary.failed > 0) {
        if (summary.successful === 0) {
          enqueueSnackbar('Failed to save attendance', { variant: 'error' });
          throw new Error('Failed');
        }
        enqueueSnackbar(`Partially saved: ${summary.successful} ok, ${summary.failed} failed`, { variant: 'warning' });
      } else {
        const msg = payload.applyToAll
          ? `Attendance marked for all students: ${summary.successful} records.`
          : `Attendance saved for ${summary.successful} student(s).`;
        enqueueSnackbar(msg, { variant: 'success' });
      }
      await loadMonthlyAttendance();
    } catch (err: any) {
      console.error('Error saving attendance:', err);
      throw err;
    }
  };

  const handleSaveOverride = async (payload: { status: AttendanceStatus; informationType?: InformationType; remarks?: string }) => {
    if (!overrideTarget) return;
    try {
      await attendanceService.overrideAttendance(overrideTarget.attendanceId, payload);
      enqueueSnackbar('Attendance updated.', { variant: 'success' });
      await loadMonthlyAttendance();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to update attendance', { variant: 'error' });
      throw err;
    }
  };

  // -----------------------------------------------------------------
  // Mark Mode helpers
  // -----------------------------------------------------------------

  // Build a fresh draft for a single visible row. Overlay cells (holiday /
  // weekly-off) are intentionally skipped — those come from the server-side
  // overlay and are never editable inline.
  //
  // `dayHasAnyRecord`: when the day already has real records for some
  // students, missing rows should NOT default to Present — that would
  // clobber the intentional partial state on save. Instead, missing rows
  // start as `cleared: true` (rendered blank; excluded from save until the
  // user clicks). For an untouched day, everyone defaults to Present so a
  // single Save can mark the whole class quickly.
  const buildDraftForRow = (
    dateStr: string,
    existing: (StudentAttendanceRecord & { _id?: string; isOverlay?: boolean }) | undefined,
    dayHasAnyRecord: boolean
  ): DraftEntry | null => {
    if (existing?.isOverlay) return null;
    const isBlank = !existing;
    const baseStatus = existing?.status ?? AttendanceStatus.PRESENT;
    return {
      status: baseStatus,
      informationType: existing?.informationType,
      remarks: existing?.remarks,
      savedStatus: existing?.status,
      savedInfoType: existing?.informationType,
      savedRemarks: existing?.remarks,
      hadSavedRecord: !!existing,
      cleared: isBlank && dayHasAnyRecord
    };
  };

  const exitEditMode = useCallback(() => {
    setEditingDate(null);
    setDraftMap({});
    setPaletteAnchor(null);
    setConfirmOverwriteOpen(false);
  }, []);

  const enterEditMode = useCallback((dateStr: string) => {
    if (!writable || !monthlyData) return;
    // Detect whether the day already has any real records — governs whether
    // students without a record default to Present or to "Not marked".
    const dayHasAnyRecord = monthlyData.studentAttendanceData.some((row) => {
      const cell = row.attendance[dateStr] as (StudentAttendanceRecord & { isOverlay?: boolean }) | undefined;
      return !!cell && !cell.isOverlay;
    });
    const next: Record<string, DraftEntry> = {};
    for (const row of monthlyData.studentAttendanceData) {
      const existing = row.attendance[dateStr] as (StudentAttendanceRecord & { _id?: string; isOverlay?: boolean }) | undefined;
      const draft = buildDraftForRow(dateStr, existing, dayHasAnyRecord);
      if (draft) next[row.student._id] = draft;
    }
    if (Object.keys(next).length === 0) {
      // Nothing to mark — likely a class-wide holiday / weekly off. Bail
      // out cleanly instead of dropping the user into a frozen edit mode.
      enqueueSnackbar('No students can be marked on this day (holiday or weekly off).', { variant: 'info' });
      return;
    }
    setDraftMap(next);
    setEditingDate(dateStr);
  }, [writable, monthlyData, enqueueSnackbar]);

  // Keep drafts in sync when the user pages while in edit mode — newly
  // visible students get a default (Present on an untouched day, or
  // "Not marked" on a day with existing records so we don't accidentally
  // overwrite the intentional partial state). Existing drafts are preserved
  // so the user doesn't lose work by hopping pages.
  useEffect(() => {
    if (!editingDate || !monthlyData) return;
    const dayHasAnyRecord = monthlyData.studentAttendanceData.some((row) => {
      const cell = row.attendance[editingDate] as (StudentAttendanceRecord & { isOverlay?: boolean }) | undefined;
      return !!cell && !cell.isOverlay;
    });
    setDraftMap(prev => {
      let mutated = false;
      const next = { ...prev };
      for (const row of monthlyData.studentAttendanceData) {
        if (next[row.student._id]) continue;
        const existing = row.attendance[editingDate] as (StudentAttendanceRecord & { _id?: string; isOverlay?: boolean }) | undefined;
        const draft = buildDraftForRow(editingDate, existing, dayHasAnyRecord);
        if (draft) {
          next[row.student._id] = draft;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [editingDate, monthlyData]);

  // Bail out of Mark Mode when scope changes — drafts are tied to a single
  // (session, class, section, date) tuple and don't make sense after the
  // user filters somewhere else.
  useEffect(() => {
    if (!editingDate) return;
    exitEditMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession, selectedClass, selectedSection, currentMonth]);

  const cycleDraftStatus = useCallback((studentId: string) => {
    setDraftMap(prev => {
      const cur = prev[studentId];
      if (!cur) return prev;
      // Cycle order: Present → Absent → Late → Half Day → Not marked →
      // back to Present. "Not marked" is a real step at the end so a single
      // click can skip a student without opening the palette.
      if (cur.cleared) {
        return {
          ...prev,
          [studentId]: {
            ...cur,
            cleared: false,
            status: AttendanceStatus.PRESENT,
            informationType: undefined
          }
        };
      }
      const isLastInCycle = cur.status === CYCLE_ORDER[CYCLE_ORDER.length - 1];
      if (isLastInCycle) {
        return {
          ...prev,
          [studentId]: {
            ...cur,
            cleared: true,
            informationType: undefined,
            remarks: undefined
          }
        };
      }
      const nextStatus = nextStatusInCycle(cur.status);
      const needsInfo = NEEDS_INFO_TYPE.has(nextStatus);
      return {
        ...prev,
        [studentId]: {
          ...cur,
          status: nextStatus,
          // Auto-default to UNINFORMED so the cycle stays one-click. The
          // popover palette is the place to switch it to INFORMED.
          informationType: needsInfo ? (cur.informationType ?? InformationType.UNINFORMED) : undefined
        }
      };
    });
  }, []);

  const applyPaletteValue = useCallback((studentId: string, value: CellPaletteValue) => {
    setDraftMap(prev => {
      const cur = prev[studentId];
      if (!cur) return prev;
      return {
        ...prev,
        [studentId]: {
          ...cur,
          status: value.status,
          informationType: value.informationType,
          remarks: value.remarks,
          // Applying a status resurrects a previously-cleared cell.
          cleared: false
        }
      };
    });
  }, []);

  // Mark this student's cell as "Not marked" for the active day. The entry
  // stays in draftMap (flagged) so the sync effect doesn't re-add a default;
  // Save filters it out entirely.
  const clearPaletteValue = useCallback((studentId: string) => {
    setDraftMap(prev => {
      const cur = prev[studentId];
      if (!cur) return prev;
      return {
        ...prev,
        [studentId]: {
          ...cur,
          cleared: true,
          informationType: undefined,
          remarks: undefined
        }
      };
    });
  }, []);

  const markAllPresentInDraft = useCallback(() => {
    setDraftMap(prev => {
      const out: Record<string, DraftEntry> = {};
      for (const [id, entry] of Object.entries(prev)) {
        out[id] = { ...entry, status: AttendanceStatus.PRESENT, informationType: undefined };
      }
      return out;
    });
  }, []);

  const draftCounts = useMemo(() => {
    const base: Record<AttendanceStatus, number> = {
      [AttendanceStatus.PRESENT]:        0,
      [AttendanceStatus.LATE]:           0,
      [AttendanceStatus.HALF_DAY]:       0,
      [AttendanceStatus.ABSENT]:         0,
      [AttendanceStatus.SCHEDULED_OFF]:  0,
      [AttendanceStatus.NOT_SCHEDULED]:  0,
      [AttendanceStatus.LEAVE]:          0,
      [AttendanceStatus.HOLIDAY]:        0
    };
    for (const entry of Object.values(draftMap)) {
      if (entry.cleared) continue;
      base[entry.status] += 1;
    }
    return base;
  }, [draftMap]);

  const changedFromSaved = useMemo(() => {
    let n = 0;
    for (const entry of Object.values(draftMap)) {
      if (!entry.hadSavedRecord) continue;
      // "Not marked" on a previously-saved cell IS a change (it soft-deletes
      // the existing record on save) — count it toward the overwrite gate.
      if (entry.cleared) { n += 1; continue; }
      const statusChanged = entry.status !== entry.savedStatus;
      const infoChanged = (entry.informationType || undefined) !== (entry.savedInfoType || undefined);
      const remarksChanged = (entry.remarks || '') !== (entry.savedRemarks || '');
      if (statusChanged || infoChanged || remarksChanged) n += 1;
    }
    return n;
  }, [draftMap]);

  const editingDateLabel = useMemo(() => {
    if (!editingDate) return '';
    try {
      return format(parseISO(editingDate), 'EEE, d MMM yyyy');
    } catch {
      return editingDate;
    }
  }, [editingDate]);

  const performDraftSave = useCallback(async () => {
    if (!editingDate || !selectedSession || !selectedClass) return;
    try {
      setSavingDraft(true);
      // Two-way split of the draft:
      //   • Non-cleared entries → upsert as usual.
      //   • Cleared entries that had a saved record → soft-delete on the
      //     backend so the cell truly becomes unmarked. Cleared entries with
      //     no saved record are pure no-ops and skipped entirely.
      const records: {
        studentId: string;
        status: AttendanceStatus;
        informationType?: InformationType;
        remarks?: string;
      }[] = [];
      const removeStudentIds: string[] = [];
      for (const [studentId, entry] of Object.entries(draftMap)) {
        if (entry.cleared) {
          if (entry.hadSavedRecord) removeStudentIds.push(studentId);
          continue;
        }
        records.push({
          studentId,
          status: entry.status,
          informationType: entry.informationType,
          remarks: entry.remarks
        });
      }
      const result = await attendanceService.markBulkAttendance({
        sessionId: selectedSession,
        classId: selectedClass,
        sectionInstanceId: selectedSection || undefined,
        date: editingDate,
        attendanceRecords: records,
        removeStudentIds: removeStudentIds.length > 0 ? removeStudentIds : undefined
      });
      const { summary } = result;
      if (summary.failed > 0 && summary.successful === 0) {
        enqueueSnackbar('Failed to save attendance', { variant: 'error' });
        return;
      }
      if (summary.failed > 0) {
        enqueueSnackbar(`Partially saved: ${summary.successful} ok, ${summary.failed} failed`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Attendance saved for ${summary.successful} student(s).`, { variant: 'success' });
      }
      exitEditMode();
      await loadMonthlyAttendance();
    } catch (err: any) {
      console.error('Error saving draft attendance:', err);
      // The downstream service throws either an Error (caught from a
      // success:false body) or an AxiosError (caught from a 4xx/5xx). The
      // backend's real message lives at err.response.data.message — fall
      // back to err.message only when that isn't there.
      const apiMessage = err?.response?.data?.message;
      enqueueSnackbar(apiMessage || err?.message || 'Failed to save attendance', { variant: 'error' });
    } finally {
      setSavingDraft(false);
    }
  }, [editingDate, selectedSession, selectedClass, selectedSection, draftMap, enqueueSnackbar, exitEditMode, loadMonthlyAttendance]);

  const handleSaveDraft = useCallback(() => {
    // If any draft would overwrite a previously-saved value, ask for an
    // explicit confirm before committing. Net-new marks skip the confirm.
    if (changedFromSaved > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    void performDraftSave();
  }, [changedFromSaved, performDraftSave]);

  const handleConfirmOverwrite = useCallback(() => {
    setConfirmOverwriteOpen(false);
    void performDraftSave();
  }, [performDraftSave]);

  // The monthly register, drawn in the browser from the whole class's month.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const handleDownloadPDF = async () => {
    if (!selectedSession || !selectedClass) {
      enqueueSnackbar('Pick a session and class first', { variant: 'warning' });
      return;
    }
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    let file = 'student-attendance';
    setDownloadingPDF(true);
    try {
      await generatePdf(async () => {
        const m = await attendanceService.getMonthlyAttendanceAll(selectedSession, selectedClass, year, month, selectedSection || undefined);
        const period = currentMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
        const cls = fmtClassName(m.class.name);
        const n = m.studentAttendanceData.length;
        file = ['student-attendance', cls, m.section?.name, period].filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        return (
          <AttendanceRegisterDocument
            title="Student Attendance Report"
            details={[
              { value: m.section ? `${cls} · Section ${m.section.name}` : cls },
              { value: period },
              { value: `${n} student${n === 1 ? '' : 's'}` },
            ]}
            subtitle={[cls, m.section?.name, period].filter(Boolean).join('  ·  ')}
            lead={[{ header: 'Roll', width: 26 }, { header: 'Student', strong: true }]}
            rows={m.studentAttendanceData.map(r => ({ id: r.student._id, cells: [r.student.rollNumber, r.student.name], attendance: r.attendance }))}
            dates={m.dates}
            branding={branding}
            generatedAt={new Date().toISOString()}
          />
        );
      }, () => `${file}.pdf`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Date-header row is computed from the month alone — NOT from the server
  // response. Keeps the columns stable across filter / search / page changes.
  const monthDates = useMemo(() => {
    return dateTimeUtils.getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
  }, [currentMonth]);

  const selectedSessionObj = useMemo(
    () => sessions.find(s => s._id === selectedSession) ?? null,
    [sessions, selectedSession]
  );

  const scopeLabel = useMemo(() => {
    const parts: string[] = [];
    const cls = classes.find(c => c._id === selectedClass);
    if (cls) parts.push(cls.name);
    const sec = sections.find(s => s._id === selectedSection);
    if (sec) parts.push(`Section ${sec.name}`);
    return parts.join(' · ');
  }, [classes, sections, selectedClass, selectedSection]);

  // ---- Permission gate ----
  if (!canViewAttendance) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight="500" gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Student Attendance.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ---- Day-cell renderer ----
  // In Mark Mode the *active column only* swaps to inline-editable behaviour:
  // single-click cycles status (P → A → L → H → P), the ⋯ pip on hover (or
  // right-click) opens the palette popover for full status / informed flag /
  // remarks. Every other column keeps the original single-cell override
  // behaviour, which the user explicitly asked to leave untouched.
  const renderDayCell = (
    studentId: string,
    studentName: string,
    rollNumber: string,
    dateStr: string,
    record: (StudentAttendanceRecord & { _id?: string; isOverlay?: boolean }) | undefined,
    isCurrentDay: boolean
  ) => {
    const isActiveColumn = editingDate === dateStr;
    const isOverlay = !!record?.isOverlay;
    const draft = isActiveColumn && !isOverlay ? draftMap[studentId] : undefined;

    // Pick the source of truth for the visual: draft in active column,
    // saved record otherwise. A cleared draft renders as empty even if a
    // previously-saved record exists — that's the whole point of "Not marked".
    const draftCleared = !!draft?.cleared;
    const displayStatus = draftCleared
      ? undefined
      : (draft ? draft.status : record?.status);
    const displayInfoType = draftCleared
      ? undefined
      : (draft ? draft.informationType : record?.informationType);

    const visual = displayStatus ? STATUS_VISUAL[displayStatus] : EMPTY_VISUAL;
    const hasInfoIndicator = !!displayStatus && !!displayInfoType && INFO_INDICATOR_STATUSES.has(displayStatus);
    const indicatorColor = displayInfoType === InformationType.INFORMED ? INFO_DOT.informed : INFO_DOT.uninformed;

    // Diff between draft and saved snapshot — surfaced as an amber border so
    // overwrites are visible at a glance before the user even hits Save. A
    // cleared draft on a previously-saved cell counts too, because Save will
    // soft-delete the existing record.
    const draftChanged = !!draft && draft.hadSavedRecord && (
      draft.cleared ||
      draft.status !== draft.savedStatus ||
      (draft.informationType || undefined) !== (draft.savedInfoType || undefined) ||
      (draft.remarks || '') !== (draft.savedRemarks || '')
    );

    // Tooltip is suppressed in the active column to keep mouse interactions
    // crisp — the palette popover is the place for remarks while editing.
    const tooltip = !isActiveColumn && record
      ? [
          record.remarks,
          record.isOverlay ? '(Overlay — no real mark)' : null
        ].filter(Boolean).join(' · ')
      : '';

    // Outside Mark Mode: the legacy single-cell override is still the way
    // to edit one record. Inside Mark Mode: only the active column responds
    // to clicks, and only for non-overlay rows that have a draft.
    const overrideEditable = !editingDate && writable && record && !record.isOverlay && record._id;
    const activeEditable = isActiveColumn && !!draft;

    const openPaletteFor = (anchor: HTMLElement) => {
      if (!draft) return;
      setPaletteAnchor({ el: anchor, studentId, studentName, rollNumber });
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (activeEditable) {
        cycleDraftStatus(studentId);
        return;
      }
      if (overrideEditable) {
        setOverrideTarget({
          attendanceId: record!._id!,
          studentName,
          rollNumber,
          date: dateStr,
          status: record!.status,
          informationType: record!.informationType,
          remarks: record!.remarks
        });
      }
    };

    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!activeEditable) return;
      e.preventDefault();
      openPaletteFor(e.currentTarget);
    };

    const interactive = activeEditable || overrideEditable;
    const cellSize = isActiveColumn ? 26 : 22;

    const cellInner = (
      <Box
        onClick={interactive ? handleClick : undefined}
        onContextMenu={activeEditable ? handleContextMenu : undefined}
        sx={{
          width: cellSize,
          height: cellSize,
          borderRadius: 1,
          backgroundColor: visual.bg === 'transparent' ? 'action.hover' : visual.bg,
          color: visual.bg === 'transparent' ? 'text.secondary' : '#fff',
          border: draftChanged ? '2px solid' : '1px solid',
          borderColor: draftChanged
            ? '#f59e0b'
            : (visual.border === 'transparent' ? 'divider' : visual.border),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 700,
          mx: 'auto',
          position: 'relative',
          cursor: interactive ? 'pointer' : (tooltip ? 'help' : 'default'),
          transition: 'transform 120ms ease, box-shadow 120ms ease, width 150ms ease, height 150ms ease, border-color 150ms ease',
          ...(activeEditable && {
            boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`
          }),
          ...(interactive && {
            '&:hover': {
              transform: 'scale(1.12)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              ...(activeEditable && {
                '& .cell-palette-pip': { opacity: 1, transform: 'scale(1)' }
              })
            }
          })
        }}
      >
        {visual.text}
        {hasInfoIndicator && (
          <Box sx={{
            position: 'absolute', top: -2, right: -2, width: 6, height: 6,
            borderRadius: '50%', backgroundColor: indicatorColor, border: '1px solid white'
          }} />
        )}
        {activeEditable && (
          <Tooltip title="More options (remarks, informed / uninformed)" arrow placement="top">
            <Box
              className="cell-palette-pip"
              onClick={(e) => {
                e.stopPropagation();
                openPaletteFor((e.currentTarget.parentElement as HTMLElement) || (e.currentTarget as HTMLElement));
              }}
              sx={{
                position: 'absolute',
                bottom: -5,
                right: -5,
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: 'background.paper',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
                lineHeight: 1,
                opacity: 0,
                transform: 'scale(0.8)',
                transition: 'opacity 120ms ease, transform 120ms ease',
                boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.common.black, 0.18)}`,
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' }
              }}
            >
              ⋯
            </Box>
          </Tooltip>
        )}
      </Box>
    );

    const wrapped = tooltip
      ? <Tooltip title={tooltip} arrow placement="top">{cellInner}</Tooltip>
      : cellInner;

    return (
      <TableCell
        key={dateStr}
        align="center"
        sx={{
          backgroundColor: (theme) => {
            if (isActiveColumn) {
              return theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.18)
                : alpha(theme.palette.primary.main, 0.08);
            }
            return isCurrentDay
              ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.10)' : 'rgba(25, 118, 210, 0.05)'
              : 'inherit';
          },
          minWidth: isActiveColumn ? '40px' : '30px',
          maxWidth: isActiveColumn ? '40px' : '30px',
          width: isActiveColumn ? '40px' : '30px',
          padding: '2px 1px',
          transition: 'min-width 180ms ease, max-width 180ms ease, width 180ms ease, background-color 200ms ease'
        }}
      >
        {wrapped}
      </TableCell>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4">Student Attendance Management</Typography>
            {scopeLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {scopeLabel}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={2}>
            {canViewAttendance && selectedSession && selectedClass && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<BusyIcon busy={downloadingPDF} size={20}><PictureAsPdf /></BusyIcon>}
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {downloadingPDF ? 'Preparing…' : 'Download PDF'}
              </Button>
            )}
            {/* Add Attendance button hidden — inline column-header Mark Mode
                covers the daily flow. Kept commented for the bulk
                "apply same status + info type + remarks to everyone" case
                that the inline bar can't do yet. Restore once that's
                covered (or remove if intentionally dropped). */}
            {/* {canMarkAttendance && (
              <Tooltip
                title={
                  !isViewingActive
                    ? 'Switch to the active session to mark attendance'
                    : (unauthorizedReason ?? '')
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Add />}
                    disabled={!writable}
                    onClick={() => {
                      if (!selectedSession || !selectedClass) {
                        enqueueSnackbar('Please select a session and class first', { variant: 'warning' });
                        return;
                      }
                      setSelectedModalDate(new Date());
                      setMarkModalOpen(true);
                    }}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Add Attendance
                  </Button>
                </span>
              </Tooltip>
            )} */}
          </Box>
        </Box>

        {/* Filters — dense layout matching EnrollmentsPage */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Session</InputLabel>
              <Select
                value={selectedSession}
                onChange={(e) => {
                  setSelectedSession(e.target.value);
                  setSelectedClass('');
                  setSelectedSection('');
                }}
                label="Session"
              >
                {sessions.map(s => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                    {activeSession?._id === s._id && (
                      <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1, fontWeight: 600 }}>
                        · Active
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }} disabled={!selectedSession}>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedSection('');
                }}
                label="Class"
              >
                {classes.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }} disabled={!selectedClass || sections.length === 0}>
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                label="Section"
              >
                {/* "All sections" only makes sense for non-Teachers — a
                    Teacher selecting it would see students from sections
                    they don't own. */}
                {!isTeacher && <MenuItem value=""><em>All sections</em></MenuItem>}
                {sections.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: { md: 'auto' } }}>
              <IconButton onClick={() => navigateMonth('prev')} size="small"><NavigateBefore /></IconButton>
              <Typography variant="body2" sx={{ minWidth: 110, textAlign: 'center', fontWeight: 600 }}>
                {format(currentMonth, 'MMMM yyyy')}
              </Typography>
              <IconButton onClick={() => navigateMonth('next')} size="small"><NavigateNext /></IconButton>
              <Tooltip title="Go to current month">
                <IconButton onClick={() => setCurrentMonth(startOfMonth(new Date()))} size="small" color="primary">
                  <Today />
                </IconButton>
              </Tooltip>
            </Box>

            <TextField
              size="small"
              placeholder="Search by name or roll number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Stack>
        </Paper>

        {/* Read-only banner for non-active sessions */}
        {selectedSession && selectedSessionObj && !isViewingActive && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Viewing past session <strong>{selectedSessionObj.name}</strong>. Attendance is read-only — switch to the active session to mark or edit.
          </Alert>
        )}

        {/* Class-teacher gate banner */}
        {isViewingActive && unauthorizedReason && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            {unauthorizedReason}
          </Alert>
        )}

        {/* No-selection state */}
        {!selectedSession || !selectedClass ? (
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select Session &amp; Class
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pick a session and class to view monthly attendance.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          /* Monthly matrix — kept mounted across filter/search/page refreshes
             so the header row and sticky student column don't flicker. The
             only signal of in-flight work is the LinearProgress strip below
             and a faint dim on the rows while `loading` is true. */
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'relative' }}>
            {/* Fixed-height container so the strip doesn't cause layout shift. */}
            <Box sx={{ height: 3, position: 'relative' }}>
              {loading && (
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3
                  }}
                />
              )}
            </Box>
            <CardContent sx={{ p: 0, opacity: loading ? 0.7 : 1, transition: 'opacity 150ms ease' }}>
              {monthlyData && monthlyData.studentAttendanceData.length > 0 ? (
                <TableContainer
                  sx={{
                    maxHeight: '75vh',
                    overflowX: 'auto',
                    '& .MuiTableCell-root': {
                      borderRight: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#e0e0e0'}`,
                      padding: '4px 2px'
                    }
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            minWidth: 220,
                            maxWidth: 220,
                            width: 220,
                            fontSize: '0.8rem',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
                            borderRight: (theme) => `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                            padding: '6px 12px'
                          }}
                        >
                          <Box sx={{ ml: 1 }}>Student</Box>
                        </TableCell>
                        {monthDates.map((dateStr) => {
                          const displayDate = dateTimeUtils.formatDisplayDate(dateStr, { showDay: false });
                          const dow = dateTimeUtils.getDayOfWeek(dateStr);
                          const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                          // "Today" must follow the school timezone — comparing
                          // against UTC would mark the wrong column for the few
                          // hours each day that PKT and UTC disagree.
                          const todayStr = getToday();
                          const isCurrentDay = dateStr === todayStr;
                          const isActiveColumn = editingDate === dateStr;
                          // Headers are clickable to enter Mark Mode when the
                          // user can write *and* no other column is already
                          // being edited. The active header doubles as the
                          // exit affordance.
                          const headerClickable =
                            writable && !savingDraft && (!editingDate || isActiveColumn);
                          const headerTooltip = !writable
                            ? ''
                            : isActiveColumn
                              ? 'Exit edit mode (drafts will be discarded)'
                              : editingDate
                                ? 'Save or cancel the current column first'
                                : 'Click to mark attendance for this day';
                          const headerLabel = (
                            <TableCell
                              key={dateStr}
                              align="center"
                              onClick={headerClickable ? () => (isActiveColumn ? exitEditMode() : enterEditMode(dateStr)) : undefined}
                              sx={{
                                fontWeight: 600,
                                minWidth: isActiveColumn ? '40px' : '30px',
                                maxWidth: isActiveColumn ? '40px' : '30px',
                                width: isActiveColumn ? '40px' : '30px',
                                fontSize: '0.65rem',
                                cursor: headerClickable ? 'pointer' : 'default',
                                backgroundColor: (theme) => {
                                  if (isActiveColumn) {
                                    return theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.28)
                                      : alpha(theme.palette.primary.main, 0.14);
                                  }
                                  return isCurrentDay
                                    ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 1)' : 'grey.100'
                                    : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100';
                                },
                                borderBottom: (theme) =>
                                  isActiveColumn
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                                padding: '4px 1px',
                                transition: 'background-color 200ms ease, min-width 180ms ease, max-width 180ms ease, width 180ms ease',
                                '&:hover': headerClickable && !isActiveColumn ? {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.18)
                                      : alpha(theme.palette.primary.main, 0.08)
                                } : undefined
                              }}
                            >
                              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    fontSize: '0.55rem',
                                    lineHeight: 1,
                                    fontWeight: 600,
                                    color: (theme) =>
                                      isActiveColumn
                                        ? theme.palette.primary.main
                                        : isCurrentDay
                                          ? theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1976d2'
                                          : theme.palette.text.secondary
                                  }}
                                >
                                  {days[dow]}
                                </Typography>
                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.25} sx={{ mt: 0.25 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      lineHeight: 1,
                                      color: (theme) =>
                                        isActiveColumn
                                          ? theme.palette.primary.main
                                          : isCurrentDay
                                            ? theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1976d2'
                                            : theme.palette.text.primary
                                    }}
                                  >
                                    {displayDate}
                                  </Typography>
                                  {isActiveColumn && (
                                    <EditCalendar sx={{ fontSize: 12, color: 'primary.main' }} />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                          );
                          return headerTooltip
                            ? <Tooltip key={dateStr} title={headerTooltip} arrow placement="top">{headerLabel}</Tooltip>
                            : headerLabel;
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyData.studentAttendanceData.map(({ student, attendance }) => {
                        const todayStr = getToday();
                        return (
                          <TableRow
                            key={student._id}
                            sx={{
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : '#f9f9f9'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#e0e0e0'}`
                              }
                            }}
                          >
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'white',
                                minWidth: 220,
                                maxWidth: 220,
                                width: 220,
                                borderRight: (theme) => `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                                padding: '6px 12px'
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                  {(student.name?.charAt(0) || '?').toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                      fontSize: '0.78rem',
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}
                                  >
                                    {student.name || '—'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', lineHeight: 1 }}
                                  >
                                    Roll #{student.rollNumber}
                                    {student.section ? ` · ${student.section}` : ''}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            {monthDates.map(dateStr =>
                              renderDayCell(
                                student._id,
                                student.name,
                                student.rollNumber,
                                dateStr,
                                attendance[dateStr] as any,
                                dateStr === todayStr
                              )
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : !loading ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Students Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? 'No students match your search. Try a different query.'
                      : 'No students enrolled in this session / class / section.'}
                  </Typography>
                </Box>
              ) : (
                /* Initial fetch — keep the card height stable while we wait
                   so the page doesn't jump when rows arrive. */
                <Box sx={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {/* Pagination */}
              {monthlyData && monthlyData.studentAttendanceData.length > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 40, 50, 100]}
                />
              )}

              {/* Legend */}
              <Box sx={{
                p: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center'
              }}>
                {[
                  AttendanceStatus.PRESENT, AttendanceStatus.LATE, AttendanceStatus.HALF_DAY,
                  AttendanceStatus.ABSENT, AttendanceStatus.SCHEDULED_OFF, AttendanceStatus.HOLIDAY,
                ].map(s => ({ color: STATUS_VISUAL[s].bg, label: `${STATUS_VISUAL[s].label} (${STATUS_VISUAL[s].text})` })).map(item => (
                  <Box key={item.label} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: '50%' }} />
                    <Typography variant="caption">{item.label}</Typography>
                  </Box>
                ))}
                <Box display="flex" alignItems="center" gap={1} sx={{ ml: 2, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: INFO_DOT.informed, borderRadius: '50%', border: '1px solid #ccc' }} />
                  <Typography variant="caption">Informed</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 6, height: 6, bgcolor: INFO_DOT.uninformed, borderRadius: '50%', border: '1px solid #ccc' }} />
                  <Typography variant="caption">Uninformed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <StudentMarkAttendanceModal
          open={markModalOpen}
          onClose={() => setMarkModalOpen(false)}
          selectedDate={selectedModalDate}
          onDateChange={setSelectedModalDate}
          onSave={handleSaveAttendance}
          onSearch={handleStudentSearch}
          scopeLabel={scopeLabel}
        />

        <StudentOverrideAttendanceModal
          open={!!overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onSave={handleSaveOverride}
          initial={overrideTarget || {}}
        />

        {/* Inline Mark Mode — floating bar appears once a day header is
            clicked. The bar is the single Save / Cancel surface for the
            staged draft column. */}
        {editingDate && (
          <AttendanceMarkBar
            dateLabel={editingDateLabel}
            counts={draftCounts}
            totalDraftable={Object.keys(draftMap).length}
            changedFromSaved={changedFromSaved}
            saving={savingDraft}
            onMarkAllPresent={markAllPresentInDraft}
            onCancel={exitEditMode}
            onSave={handleSaveDraft}
          />
        )}

        {/* Per-cell palette — right-click or ⋯ pip on hover. Stages back
            into the page-level draftMap; never hits the server directly. */}
        <AttendanceCellPalette
          open={!!paletteAnchor}
          anchorEl={paletteAnchor?.el ?? null}
          title={paletteAnchor?.studentName ?? ''}
          subtitle={paletteAnchor ? `Roll #${paletteAnchor.rollNumber}` : undefined}
          initial={(() => {
            if (!paletteAnchor) return { status: AttendanceStatus.PRESENT };
            const cur = draftMap[paletteAnchor.studentId];
            return cur
              ? { status: cur.status, informationType: cur.informationType, remarks: cur.remarks }
              : { status: AttendanceStatus.PRESENT };
          })()}
          onClose={() => setPaletteAnchor(null)}
          onApply={(value) => {
            if (!paletteAnchor) return;
            applyPaletteValue(paletteAnchor.studentId, value);
            setPaletteAnchor(null);
          }}
          onClear={() => {
            if (!paletteAnchor) return;
            clearPaletteValue(paletteAnchor.studentId);
            setPaletteAnchor(null);
          }}
        />

        {/* Overwrite confirm — only opens when Save would replace one or
            more previously-saved records. Net-new marks skip this gate. */}
        <Dialog
          open={confirmOverwriteOpen}
          onClose={() => setConfirmOverwriteOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Overwrite saved attendance?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>{changedFromSaved}</strong> record{changedFromSaved === 1 ? '' : 's'} will replace values that were already saved for <strong>{editingDateLabel}</strong>. The audit trail keeps the old values, but this change is visible immediately.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmOverwriteOpen(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmOverwrite}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              Overwrite & Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AttendancePage;
